import React, { useContext, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";

import { AuthContext } from "../../context/Auth/AuthContext";
import ChatAvatar from "./ChatAvatar";
import {
  formatListTime,
  formatPreview,
  getChatTitle,
  isDirectChat,
  lastMessageMentions,
  unreadsFor,
} from "./chatUtils";

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "unread", label: "Não lidas" },
  { key: "groups", label: "Grupos" },
  { key: "direct", label: "Diretas" },
];

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accentText = isDark ? "#8DB8F5" : "#1A4FA8";
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      minHeight: 0,
      color: theme.palette.text.primary,
    },
    top: {
      padding: theme.spacing(2, 2, 1.5),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    searchRow: {
      display: "flex",
      gap: theme.spacing(1),
      alignItems: "center",
    },
    search: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      height: 40,
      padding: theme.spacing(0, 1.5),
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
      "&:focus-within": {
        borderColor: theme.palette.primary.main,
      },
    },
    searchInput: {
      flex: 1,
      fontSize: 13,
      color: theme.palette.text.primary,
    },
    newButton: {
      width: 40,
      height: 40,
      borderRadius: 8,
      backgroundColor: "#2468D4",
      color: "#fff",
      "&:hover": { backgroundColor: "#1A4FA8" },
    },
    filters: {
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
    },
    filter: {
      height: 30,
      padding: theme.spacing(0, 1.5),
      borderRadius: 15,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: "transparent",
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 500,
      fontFamily: "inherit",
      cursor: "pointer",
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    filterActive: {
      borderColor: "#2468D4",
      backgroundColor: isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC",
      color: accentText,
      fontWeight: 600,
    },
    list: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      width: "100%",
      padding: theme.spacing(1.5, 2),
      border: 0,
      background: "transparent",
      textAlign: "left",
      cursor: "pointer",
      fontFamily: "inherit",
      color: theme.palette.text.primary,
      "&:hover": { backgroundColor: theme.palette.action.hover },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: -2,
      },
    },
    itemSelected: {
      backgroundColor: isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC",
      "&:hover": {
        backgroundColor: isDark ? "rgba(45,123,232,0.24)" : "#DCE8FB",
      },
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 3,
    },
    line: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    title: {
      flex: 1,
      fontSize: 14,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    time: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    preview: {
      flex: 1,
      fontSize: 12,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    typingText: {
      flex: 1,
      fontSize: 12,
      fontWeight: 600,
      color: isDark ? "#7FD6A6" : "#157A47",
    },
    at: {
      width: 20,
      height: 20,
      borderRadius: 10,
      backgroundColor: isDark ? "rgba(240,163,94,0.25)" : "#FDEBD7",
      color: isDark ? "#F5B27A" : "#8A3A00",
      fontSize: 12,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    unreadTitle: { fontWeight: 700 },
    unreadTime: { color: accentText, fontWeight: 600 },
    unreadPreview: { color: theme.palette.text.primary, fontWeight: 500 },
    badge: {
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      boxSizing: "border-box",
      borderRadius: 10,
      backgroundColor: "#2468D4",
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    empty: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(6, 3),
      color: theme.palette.text.secondary,
    },
    emptyIcon: { fontSize: 44, opacity: 0.35 },
    loading: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(2),
    },
  };
});

export default function ChatList({
  chats,
  typing = {},
  currentChatId,
  loading,
  hasMore,
  onLoadMore,
  onSelectChat,
  onNewChat,
}) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const unreadCount = useMemo(
    () => chats.filter((c) => unreadsFor(c, user.id) > 0).length,
    [chats, user.id]
  );

  const visibleChats = useMemo(() => {
    const term = search.trim().toLowerCase();
    return chats.filter((chat) => {
      if (filter === "unread" && unreadsFor(chat, user.id) === 0) return false;
      if (filter === "groups" && isDirectChat(chat)) return false;
      if (filter === "direct" && !isDirectChat(chat)) return false;
      if (!term) return true;
      if (getChatTitle(chat, user.id).toLowerCase().includes(term)) return true;
      return (chat.users || []).some((u) =>
        (u.user?.name || "").toLowerCase().includes(term)
      );
    });
  }, [chats, filter, search, user.id]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 120) onLoadMore();
  };

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <div className={classes.searchRow}>
          <label className={classes.search}>
            <SearchIcon style={{ fontSize: 18, opacity: 0.6 }} />
            <InputBase
              className={classes.searchInput}
              placeholder="Buscar conversa ou pessoa"
              inputProps={{ "aria-label": "Buscar conversa ou pessoa" }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <Tooltip title="Nova conversa">
            <IconButton
              className={classes.newButton}
              onClick={onNewChat}
              aria-label="Nova conversa"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.filters}>
          {FILTERS.map((f) => (
            <button
              type="button"
              key={f.key}
              aria-pressed={filter === f.key}
              className={`${classes.filter} ${
                filter === f.key ? classes.filterActive : ""
              }`}
              onClick={() => setFilter(f.key)}
            >
              {f.key === "unread" && unreadCount > 0
                ? `${f.label} · ${unreadCount}`
                : f.label}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.list} onScroll={handleScroll}>
        {!loading && chats.length === 0 && (
          <div className={classes.empty}>
            <ChatBubbleOutlineIcon className={classes.emptyIcon} />
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Nenhuma conversa ainda
            </Typography>
            <Typography variant="caption">
              Converse com a equipe sem sair do atendimento.
            </Typography>
            <Button
              color="primary"
              variant="contained"
              disableElevation
              onClick={onNewChat}
              style={{ marginTop: 8, textTransform: "none", fontWeight: 600 }}
            >
              Nova conversa
            </Button>
          </div>
        )}

        {chats.length > 0 && visibleChats.length === 0 && (
          <div className={classes.empty}>
            <Typography variant="body2">Nenhuma conversa encontrada.</Typography>
          </div>
        )}

        {visibleChats.map((chat) => {
          const unreads = unreadsFor(chat, user.id);
          const selected = chat.id === currentChatId;
          const hasUnread = unreads > 0 && !selected;
          const typingNames = Object.values(typing[chat.id] || {});
          return (
            <button
              type="button"
              key={chat.id}
              onClick={() => onSelectChat(chat)}
              aria-current={selected ? "true" : undefined}
              className={`${classes.item} ${
                selected ? classes.itemSelected : ""
              }`}
            >
              <ChatAvatar chat={chat} userId={user.id} />
              <span className={classes.itemBody}>
                <span className={classes.line}>
                  <span
                    className={`${classes.title} ${
                      hasUnread ? classes.unreadTitle : ""
                    }`}
                  >
                    {getChatTitle(chat, user.id)}
                  </span>
                  <span
                    className={`${classes.time} ${
                      hasUnread ? classes.unreadTime : ""
                    }`}
                  >
                    {chat.lastMessage ? formatListTime(chat.updatedAt) : ""}
                  </span>
                </span>
                <span className={classes.line}>
                  {typingNames.length > 0 ? (
                    <span className={classes.typingText}>
                      {isDirectChat(chat)
                        ? "digitando…"
                        : `${typingNames[0].split(" ")[0]} digitando…`}
                    </span>
                  ) : (
                    <span
                      className={`${classes.preview} ${
                        hasUnread ? classes.unreadPreview : ""
                      }`}
                    >
                      {formatPreview(chat, user)}
                    </span>
                  )}
                  {hasUnread && lastMessageMentions(chat, user) && (
                    <span className={classes.at} title="Você foi mencionado">
                      @
                    </span>
                  )}
                  {hasUnread && (
                    <span
                      className={classes.badge}
                      aria-label={`${unreads} não lidas`}
                    >
                      {unreads > 99 ? "99+" : unreads}
                    </span>
                  )}
                </span>
              </span>
            </button>
          );
        })}

        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={22} />
          </div>
        )}
      </div>
    </div>
  );
}
